<script setup>
/**
 * 問題列表元件 (QusList.vue)
 *
 * 顯示目前講座 / 課程的問題列表，支援分頁、切換「目前講座 / 最新 20 筆」、
 * RPG 帳號綁定或登入入口，以及「提出新問題」導頁。
 *
 * @fires changeView - 切換畫面（list / new / detail）
 * @fires getOk - 問題列表載入成功
 */
import questionsAPI from '../apis/questions';
import Loading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/vue-loading.css';
import {
  ref, reactive, onMounted, watch, defineEmits, defineProps,
} from 'vue';
import Pagination from 'v-pagination-3';

// 因為改變 delimiters 出現的問題
// eslint-disable-next-line no-unused-vars
import { getDiff, questionType } from '../utils/format';
// eslint-disable-next-line no-unused-vars
import { getCookie } from '../utils/cookie';
import { useToast } from '../composables/useToast';
import { useSession } from '../composables/useSession';
// eslint-disable-next-line no-unused-vars
import { directToRPGUserSystem } from '../services/auth';

const { postMessage } = useToast();
const { actionPermission } = useSession();

const emit = defineEmits(['changeView', 'getOk']);
const props = defineProps({
  token: {
    type: String,
    default: '',
  },
  isLink: Boolean,
  refresh: Boolean,
});

const isLoading = ref(false);
const readNotice = ref(false); // 讀過發問規範
watch(readNotice, (value) => {
  window.localStorage.setItem('hex_teachable_notice_read', value);
});

const data = reactive({
  questions: [],
  pagination: {},
  courseQuestionCount: 300,
});

const courseId = ref('');
const videoCourseId = ref('');

/**
 * 依講座 / 課程取得問題列表並更新 `data`，成功後 emit `getOk`。
 * @param {number} [page=1] - 分頁頁碼
 */
const getQuestionList = (page = 1) => {
  isLoading.value = true;
  questionsAPI.apiGetLectureQuestions(videoCourseId.value, courseId.value, page).then((res) => {
    data.questions = res.data.questions;
    data.pagination = res.data.pagination;
    data.courseQuestionCount = res.data.courseQuestionCount;
    isLoading.value = false;
    emit('getOk');
  }).catch(() => {
    isLoading.value = false;
  });
};

onMounted(() => {
  isLoading.value = true;
  courseId.value = document.getElementById('lecture_heading').dataset.lectureId;
  videoCourseId.value = document.getElementById('lecture_heading').dataset.lectureUrl.split('/')[2];
  readNotice.value = window.localStorage.getItem('hex_teachable_notice_read') === 'true';
  getQuestionList();
});
watch(() => props.refresh, (selection) => {
  if (selection) {
    getQuestionList();
    emit('changeView', { type: 'list', id: -1, refresh: false });
  }
});
watch(() => props.token, (value) => {
  if (value) {
    getQuestionList();
  }
});

/**
 * 移除 HTML 標籤並截斷為預覽字串（前 60 字加省略號）。
 * @param {string} body - 含 HTML 的問題內容
 * @returns {string} 純文字預覽
 */
const splitString = (body) => {
  const regex = /(<([^>]+)>)/gi;
  const results = body.replace(regex, '');
  return `${results.slice(0, 60)}...`;
};

/**
 * 取得此課程最新 20 筆問題（不分頁），並清空分頁資訊。
 */
const getCurrentTwentyData = () => {
  questionsAPI.apiGetVideoCourseQuestions(videoCourseId.value).then((res) => {
    data.questions = res.data.questions;
    data.pagination = {};
  }).catch(error => {
    postMessage('danger', '取得最新 20 筆資料時發生錯誤', `${error?.response?.status} - ${error?.response?.data.message}`);
  });
};

const currentMenu = ref('chapter'); // 目前講座: chapter, 最近二十筆 recent
watch(() => currentMenu.value, (value) => {
  if (value === 'chapter') {
    getQuestionList();
  } else {
    getCurrentTwentyData();
  }
});

/**
 * 依 cookie 中的綁定狀態導向 RPG 使用者系統（已綁定走登入、未綁定走綁定流程）。
 */
const openLoginUrl = () => {
  const isLink = getCookie('rpg_temp_token_is_link') === 'true';
  if (isLink) {
    directToRPGUserSystem(true);
  } else {
    directToRPGUserSystem(false);
  }
}
</script>

<template>
  <div class="d-flex align-items-start">
    <div class="d-flex">
      <h2 class="text-nowrap mb-0 me-3">
        問題列表
      </h2>
      <select
        v-model="currentMenu"
        class="form-select"
        aria-label="Default select example"
      >
        <option value="chapter">
          目前講座
        </option>
        <option value="recent">
          此課程最新 20 筆
        </option>
      </select>
    </div>
    <div class="ms-auto d-flex align-items-start">
      <div class="d-flex flex-column">
        <button
          class="btn btn-hex-primary px-4 rounded-0"
          :disabled="!readNotice || !actionPermission"
          @click="$emit('changeView', {type: 'new', id: -1, refresh: false })"
        >
          提出新問題
        </button>
        <div class="form-check mt-2">
          <input
            id="readNoticeCheckbox"
            v-model="readNotice"
            class="form-check-input"
            type="checkbox"
            value=""
          >
          <label
            class="form-check-label"
            for="readNoticeCheckbox"
          >
            我已詳讀<a
              href="https://www.notion.so/94eb3ec6f7c54492a5878a0dc58c74e1"
              target="_blank"
            >發問規範</a>
          </label>
        </div>
      </div>
      <button
        v-if="!actionPermission"
        @click="openLoginUrl"
        class="btn btn-dark px-4 rounded-0 ms-2"
      >
        <span v-if="!props.isLink">綁定</span>
        <span v-else>登入</span>
        RPG 帳號
      </button>
    </div>
  </div>
  <p class="my-3 text-muted">
    此章節問題數 ${data.pagination.total} / 此課程問題數 ${ data.courseQuestionCount }
  </p>
  <div class="position-relative">
    <loading
      :active="isLoading"
      :is-full-page="false"
    />
    <div
      class="list-group"
      style="min-height: 200px;"
    >
      
      <a
        v-for="q in data.questions"
        :id="q.id"
        :key="q.id"
        href="#"
        class="list-group-item list-group-item-action h-list-group-item p-3"
        aria-current="true"
        @click.prevent="$emit('changeView', {type: 'detail', id: q.id, refresh: false})"
      >
        <div class="d-flex align-items-start">
          <img
            :src="q.avatar || 'https://s.gravatar.com/avatar/9024444d63ae53512c40d2a60da3d6a3?d=mm'"
            alt=""
            width="48"
            height="48"
            style="object-fit: cover"
            class="rounded-circle"
          >
          <div class="mt-2 mx-3">
            <h5 class="mb-1">${ q.title }</h5>
            <div
              class="mb-4 text-muted"
              v-html="splitString(q.body)"
            />
            <small class="text-muted">${ q.nickname } / ${getDiff(q.create_time)} / ${q.video_course_lecture_name} / ${ questionType(q.question_type_name) }</small>
          </div>
          <div class="ms-auto text-nowrap">
            <small class="d-flex align-items-center">
              ${q.replies} <span class="ms-1 material-icons">
                chat_bubble_outline
              </span>
            </small>
            <small class="d-flex align-items-center mt-2">
              ${q.upvotes} <span class="ms-1 material-icons">
                thumb_up_off_alt
              </span>
            </small>
          </div>
        </div>
      </a>
    </div>
    <div
      v-if="currentMenu === 'chapter' && data.pagination && data.pagination.current_page"
      class="my-3"
    >
      <Pagination
        v-model="data.pagination.current_page"
        :records="parseInt(data.pagination.total)"
        :per-page="parseInt(data.pagination.count)"
        :hide-count="true"
        @paginate="getQuestionList"
      />
    </div>
  </div>
</template>

<style scoped>
</style>

<spec lang="md">
# 問題列表 (QusList.vue)

## 元件概述
- **用途**：顯示目前講座 / 課程的問題列表，提供分頁、資料來源切換、發問入口與 RPG 帳號綁定 / 登入按鈕。

## Props
| 名稱 | 類型 | 必填 | 預設值 | 說明 |
|-----|------|-----|--------|------|
| token | `string` | 否 | `''` | 使用者 token，變動時重新取得列表 |
| isLink | `boolean` | 否 | `false` | 是否已綁定 RPG 帳號（影響按鈕文字：綁定／登入） |
| refresh | `boolean` | 否 | `false` | 為 `true` 時重新取得列表並通知父層重置 |

## Emits
| 事件名稱 | 參數 | 說明 |
|---------|-----|------|
| changeView | `({ type, id, refresh })` | 切換至新增問題或問題詳情畫面 |
| getOk | `()` | 問題列表載入成功 |

## Slots
無

## 功能行為
- `onMounted` 由 `#lecture_heading` 的 `data-lecture-id` 取得 `courseId`、`data-lecture-url` 取得 `videoCourseId`
- 由 localStorage `hex_teachable_notice_read` 還原「已讀發問規範」勾選狀態，勾選變動時寫回
- `currentMenu` 切換資料來源：`chapter` 呼叫 `getQuestionList()`、`recent` 呼叫 `getCurrentTwentyData()`
- 監聽 `props.refresh`：為 `true` 時重新取得列表並 emit `changeView` 以重置 refresh
- 監聽 `props.token`：有值時重新取得列表
- 「提出新問題」按鈕需已讀發問規範（`readNotice`）且具操作權限（`actionPermission`）才可點擊
- 無操作權限時顯示「綁定 / 登入 RPG 帳號」按鈕
- 使用 `v-pagination-3` 進行分頁（僅 `chapter` 模式）

## 測試行為
- 應顯示問題列表，每筆顯示標題、暱稱、時間、分類、回覆數與讚數
- `readNotice` 為 false 或 `actionPermission` 為 false 時，「提出新問題」按鈕應禁用
- `actionPermission` 為 false 時，應顯示綁定 / 登入按鈕；`isLink` 為 false 顯示「綁定」、true 顯示「登入」
- 點擊問題項目時，應 emit `changeView` 並帶 `type: 'detail'` 與該問題 id
- 列表載入成功時，應 emit `getOk`
- `currentMenu` 切換為 `recent` 時，應呼叫 `getCurrentTwentyData()`
- `props.refresh` 變為 true 時，應重新取得列表
- `splitString` 應移除 HTML 標籤並截斷為 60 字加省略號

## UI 規格
- 標題「問題列表」搭配資料來源 `select`（目前講座 / 最新 20 筆）
- 使用 Bootstrap `list-group`，每筆 `list-group-item` 顯示頭像、標題、內容摘要與統計數字
- 載入時以 `vue-loading-overlay` 顯示區塊級 Loading（`:is-full-page="false"`）
- 分頁使用 `Pagination`（v-pagination-3），`hide-count` 為 `true`
</spec>

